import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { deleteAdminContent, getAdminContents } from "../../api/adminApi";

const PAGE_SIZE = 20;

const normalizeContentsResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.contents)) return data.contents;
  if (Array.isArray(data?.data?.contents)) return data.data.contents;
  return [];
};

const normalizeMetaResponse = (data, page, pageSize, contentsLength) => {
  const meta = data?.meta || data?.data?.meta || {};
  const total = Number.isFinite(Number(meta.total))
    ? Number(meta.total)
    : contentsLength;
  const totalPages = Number.isFinite(Number(meta.totalPages))
    ? Math.max(Number(meta.totalPages), 1)
    : Math.max(Math.ceil(total / pageSize), 1);

  return {
    total,
    returned: Number.isFinite(Number(meta.returned))
      ? Number(meta.returned)
      : contentsLength,
    limit: Number.isFinite(Number(meta.limit)) ? Number(meta.limit) : pageSize,
    page: Number.isFinite(Number(meta.page)) ? Number(meta.page) : page,
    totalPages,
    hasPreviousPage:
      typeof meta.hasPreviousPage === "boolean" ? meta.hasPreviousPage : page > 1,
    hasNextPage:
      typeof meta.hasNextPage === "boolean"
        ? meta.hasNextPage
        : page < totalPages,
  };
};

const formatPersianDateTime = (value) => {
  if (!value) return "بدون زمان";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "بدون زمان";

  return date.toLocaleString("fa-IR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusLabel = (status) => {
  if (status === "published") return "منتشر شده";
  if (status === "scheduled") return "زمان‌بندی‌شده";
  return "پیش‌نویس";
};

const getStatusClass = (status) => {
  if (status === "published") return "status-pill published";
  if (status === "scheduled") return "status-pill scheduled";
  return "status-pill draft";
};

const getTypeLabel = (type) => {
  if (type === "course") return "دوره";
  return "خبر";
};

function AdminContentArchive() {
  const [contents, setContents] = useState([]);
  const [meta, setMeta] = useState({
    total: 0,
    returned: 0,
    limit: PAGE_SIZE,
    page: 1,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    type: "",
    status: "",
    search: "",
  });

  const fetchContents = async (requestedPage = page) => {
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("staffToken");

      if (!token) {
        window.location.href = "/admin/login";
        return;
      }

      const data = await getAdminContents({
        type: filters.type,
        status: filters.status,
        page: requestedPage,
        limit: PAGE_SIZE,
      });

      const nextContents = normalizeContentsResponse(data);
      const nextMeta = normalizeMetaResponse(
        data,
        requestedPage,
        PAGE_SIZE,
        nextContents.length
      );

      if (requestedPage > 1 && nextContents.length === 0 && nextMeta.total > 0) {
        setPage((prev) => Math.max(prev - 1, 1));
        return;
      }

      setContents(nextContents);
      setMeta(nextMeta);
    } catch (err) {
      console.error("Fetch content archive error:", err);
      setError(err.message || "خطا در دریافت آرشیو مطالب");
      setContents([]);
      setMeta((prev) => ({
        ...prev,
        total: 0,
        returned: 0,
        totalPages: 1,
        hasPreviousPage: false,
        hasNextPage: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContents(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.type, filters.status]);

  const filteredContents = useMemo(() => {
    const searchValue = filters.search.trim().toLowerCase();

    return contents.filter((item) => {
      const searchableText = [
        item?.title,
        item?.slug,
        item?.category,
        item?.summary,
        ...(Array.isArray(item?.tags) ? item.tags : []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchValue ? searchableText.includes(searchValue) : true;
    });
  }, [contents, filters.search]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "type" || name === "status") {
      setPage(1);
    }
  };

  const handleRefresh = () => {
    setMessage("");
    fetchContents(page);
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem("staffToken");

    if (!token) {
      setError("دسترسی غیرمجاز است. لطفاً وارد پنل شوید.");
      return;
    }

    const confirmed = window.confirm("آیا از حذف این محتوا مطمئن هستید؟");

    if (!confirmed) return;

    try {
      setDeletingId(id);
      setMessage("");
      setError("");

      await deleteAdminContent(id);
      setMessage("محتوا با موفقیت حذف شد.");
      await fetchContents(page);
    } catch (err) {
      console.error("Delete archive content error:", err);
      setError(err.message || "خطا در حذف محتوا");
    } finally {
      setDeletingId(null);
    }
  };

  const goToPage = (nextPage) => {
    const safePage = Math.min(Math.max(nextPage, 1), meta.totalPages || 1);
    setPage(safePage);
  };

  return (
    <div className="admin-page">
      <div className="admin-shell">
        <div className="admin-page-header">
          <div>
            <span className="admin-badge">پنل مدیریت</span>
            <h1>آرشیو مطالب</h1>
            <p>
              تمام محتواهای تولیدشده در این بخش نمایش داده می‌شود. هر صفحه ۲۰
              مطلب دارد و برای هر مطلب امکان مشاهده، ویرایش و حذف فعال است.
            </p>
          </div>

          <div className="admin-header-actions">
            <Link to="/admin/content" className="admin-back-link">
              مدیریت اخبار و محتوا
            </Link>

            <Link to="/admin/dashboard" className="admin-back-link">
              بازگشت به داشبورد
            </Link>
          </div>
        </div>

        {message && <div className="admin-message success">{message}</div>}
        {error && <div className="admin-message error">{error}</div>}

        <section className="admin-content-list admin-archive-panel">
          <div className="admin-list-header">
            <div>
              <h2>همه مطالب</h2>
              <p className="admin-archive-count">
                صفحه {page.toLocaleString("fa-IR")} از{" "}
                {(meta.totalPages || 1).toLocaleString("fa-IR")} — نمایش{" "}
                {filteredContents.length.toLocaleString("fa-IR")} مورد از کل{" "}
                {meta.total.toLocaleString("fa-IR")} محتوا
              </p>
            </div>

            <button
              type="button"
              onClick={handleRefresh}
              className="admin-secondary-btn"
              disabled={loading}
            >
              {loading ? "در حال بروزرسانی..." : "بروزرسانی"}
            </button>
          </div>

          <div className="admin-archive-filters">
            <div className="admin-field">
              <label>جستجو در همین صفحه</label>
              <input
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="جستجو در عنوان، اسلاگ، دسته‌بندی یا برچسب"
              />
            </div>

            <div className="admin-field">
              <label>نوع محتوا</label>
              <select name="type" value={filters.type} onChange={handleFilterChange}>
                <option value="">همه</option>
                <option value="news">خبر</option>
                <option value="course">دوره آموزشی</option>
              </select>
            </div>

            <div className="admin-field">
              <label>وضعیت انتشار</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">همه</option>
                <option value="draft">پیش‌نویس</option>
                <option value="published">منتشر شده</option>
                <option value="scheduled">زمان‌بندی‌شده</option>
              </select>
            </div>
          </div>

          {loading && (
            <div className="admin-empty">در حال دریافت آرشیو مطالب...</div>
          )}

          {!loading && contents.length === 0 && (
            <div className="admin-empty">هنوز محتوایی ثبت نشده است.</div>
          )}

          {!loading && contents.length > 0 && filteredContents.length === 0 && (
            <div className="admin-empty">
              محتوایی با عبارت جستجوی انتخاب‌شده در این صفحه پیدا نشد.
            </div>
          )}

          {!loading && filteredContents.length > 0 && (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>عنوان</th>
                    <th>نوع</th>
                    <th>وضعیت</th>
                    <th>زمان انتشار</th>
                    <th>دسته‌بندی</th>
                    <th>مشاهده</th>
                    <th>عملیات</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredContents.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <strong>{item.title}</strong>
                        <span>{item.slug}</span>

                        {Array.isArray(item.tags) && item.tags.length > 0 && (
                          <div className="admin-table-tags">
                            {item.tags.slice(0, 5).map((tag) => (
                              <small key={tag}>#{tag}</small>
                            ))}
                          </div>
                        )}
                      </td>

                      <td>{getTypeLabel(item.type)}</td>

                      <td>
                        <span className={getStatusClass(item.status)}>
                          {getStatusLabel(item.status)}
                        </span>
                      </td>

                      <td>
                        <small className="admin-scheduled-time">
                          {formatPersianDateTime(item.publishAt || item.createdAt)}
                        </small>
                      </td>

                      <td>{item.category}</td>

                      <td>
                        {item.status === "published" && item.slug ? (
                          <Link
                            to={`/${
                              item.type === "course" ? "courses" : "news"
                            }/${item.slug}`}
                            className="admin-secondary-btn"
                            target="_blank"
                            rel="noreferrer"
                          >
                            مشاهده سایت
                          </Link>
                        ) : (
                          <small className="admin-scheduled-time">
                            منتشر نشده
                          </small>
                        )}
                      </td>

                      <td>
                        <div className="admin-row-actions">
                          <Link
                            to={`/admin/content?edit=${item._id}`}
                            className="admin-edit-btn"
                          >
                            ویرایش
                          </Link>

                          <button
                            type="button"
                            onClick={() => handleDelete(item._id)}
                            className="admin-delete-btn"
                            disabled={deletingId === item._id}
                          >
                            {deletingId === item._id ? "در حال حذف..." : "حذف"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && meta.totalPages > 1 && (
            <div className="admin-pagination">
              <button
                type="button"
                className="admin-page-btn"
                onClick={() => goToPage(page - 1)}
                disabled={!meta.hasPreviousPage}
              >
                صفحه قبل
              </button>

              <span>
                صفحه {page.toLocaleString("fa-IR")} از{" "}
                {meta.totalPages.toLocaleString("fa-IR")}
              </span>

              <button
                type="button"
                className="admin-page-btn"
                onClick={() => goToPage(page + 1)}
                disabled={!meta.hasNextPage}
              >
                صفحه بعد
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default AdminContentArchive;
